<template>
	<div
		v-if="visible"
		:class="{'breadcrumb-inline-wrap': inline}">

		<!-- Mobile (xs): freccia back + crumb genitore; senza genitore (tab principale
			di sezione) mostra la pagina corrente, così l'altezza non cambia mai -->
		<div
			v-if="$vuetify.breakpoint.xsOnly"
			class="breadcrumb-mobile d-flex align-center px-1">
			<v-btn
				v-if="parent"
				icon
				small
				aria-label="Torna indietro"
				@click="goToCrumb(parent)">
				<v-icon>arrow_back</v-icon>
			</v-btn>
			<span class="breadcrumb-mobile-label text-truncate" :class="{'pl-2': !parent}">{{ mobileLabel }}</span>
		</div>

		<!-- Desktop/tablet: trail completo con overflow -->
		<v-breadcrumbs
			v-else
			:items="visibleItems"
			:class="inline ? 'breadcrumb-inline' : ['breadcrumb-nav', {'elevation-1': $vuetify.theme.dark}]">
			<template v-slot:divider>
				<v-icon small>mdi-chevron-right</v-icon>
			</template>
			<template v-slot:item="{ item }">

				<v-breadcrumbs-item v-if="item.overflow" class="breadcrumb-item">
					<v-menu offset-y>
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								v-bind="attrs"
								icon
								x-small
								class="breadcrumb-overflow"
								aria-label="Mostra livelli intermedi"
								v-on="on">
								<v-icon small>mdi-dots-horizontal</v-icon>
							</v-btn>
						</template>
						<v-list dense>
							<v-list-item
								v-for="(hidden, i) in item.hidden"
								:key="i"
								@click="goToCrumb(hidden.crumb)">
								<v-list-item-title>{{ hidden.label }}</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>
				</v-breadcrumbs-item>

				<v-breadcrumbs-item
					v-else
					:to="item.disabled ? undefined : item.crumb.fullPath"
					:disabled="item.disabled"
					exact
					class="breadcrumb-item">
					{{ item.label }}
				</v-breadcrumbs-item>

			</template>
		</v-breadcrumbs>

	</div>
</template>

<script>
// Quanti crumb mostrare al massimo prima di collassare i centrali in "..."
const MAX_VISIBLE_CRUMBS = 5
const TAIL_VISIBLE_CRUMBS = 3

export default {
	name: 'Breadcrumb',
	props: {
		// variante per l'app bar: nessuno sfondo/bordo, occupa solo spazio orizzontale
		inline: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		trail() {
			return this.$store.getters.breadcrumbTrail
		},
		// label delle voci di menu di sx per slug di sezione (es. ddts -> "DDT"):
		// il crumb di sezione deve corrispondere sempre alla voce di menu
		menuSectionLabels() {
			const permissions = this.$store.getters.getPermissions
			const list = Array.isArray(permissions) ? permissions : Object.values(permissions || {})
			const labels = {}
			list.forEach(item => {
				if (item && item.menu && item.router && item.trans && !labels[item.router]) {
					labels[item.router] = item.trans
				}
			})
			return labels
		},
		parent() {
			// il genitore per il back mobile: mai la sezione se ha lo stesso path
			// della pagina corrente (tab principale di sezione)
			const trail = this.trail
			if (trail.length < 2) return null
			const parent = trail[trail.length - 2]
			return parent.path === trail[trail.length - 1].path ? null : parent
		},
		visible() {
			return this.$store.getters.isAuthenticated
				&& this.$store.getters.breadcrumbVisible
		},
		// label della barra mobile: la destinazione del back se c'è, altrimenti
		// la pagina corrente
		mobileLabel() {
			if (this.parent) return this.resolveLabel(this.parent)
			const labeled = this.labeledTrail
			return labeled.length ? labeled[labeled.length - 1].label : ''
		},
		// trail con label già risolte, dedup di adiacenza ("DDT di Vendita > #39491"
		// invece di "... > DDT di Vendita #39491") e collasso delle label identiche
		// adiacenti (es. "Aziende > Aziende" -> "Aziende")
		labeledTrail() {
			const labeled = this.trail.map((crumb, i) => ({
				crumb,
				label: this.resolveLabel(crumb, i > 0 ? this.trail[i - 1] : null),
			}))
			return labeled.filter((entry, i) => !(i < labeled.length - 1 && entry.label === labeled[i + 1].label))
		},
		visibleItems() {
			const trail = this.labeledTrail
			const last = this.trail[this.trail.length - 1]
			const items = entries => entries.map(entry => ({
				...entry,
				// disabilitato anche il crumb di sezione che punta alla pagina corrente
				// (tab principale: stesso path dell'index)
				disabled: entry.crumb === last || entry.crumb.path === last.path,
			}))

			if (trail.length <= MAX_VISIBLE_CRUMBS) {
				return items(trail)
			}

			return [
				...items([trail[0]]),
				{ overflow: true, hidden: trail.slice(1, trail.length - TAIL_VISIBLE_CRUMBS) },
				...items(trail.slice(trail.length - TAIL_VISIBLE_CRUMBS)),
			]
		},
	},
	methods: {
		resolveLabel(crumb, prev = null) {
			if (!crumb) return ''
			if (crumb.entityLabel) return crumb.entityLabel
			if (crumb.type === 'section') {
				const slug = crumb.path.split('/').filter(Boolean)[0]
				// labelKey uguale allo slug = nessun override nel registry: vince la voce di menu
				if (crumb.labelKey === slug) {
					return this.menuSectionLabels[slug] || this.$trans(slug)
				}
				return this.$trans(crumb.labelKey)
			}
			let label = this.$trans(crumb.labelKey)
			if (crumb.type === 'show') {
				const segments = crumb.path.split('/')
				const id = segments[segments.length - 1]
				if (/^\d+$/.test(id)) {
					// se il crumb precedente è l'index dello stesso modello il contesto c'è già:
					// basta l'id ("Preventivi > #9030"). Arrivando da un altro modello la label
					// resta completa per non creare ambiguità.
					const slug = crumb.name && crumb.name.endsWith('-show') ? crumb.name.slice(0, -5) : null
					const prevIsSameModelIndex = prev && !prev.entityLabel && (
						(slug && prev.name === slug + '-index')
						|| this.$trans(prev.labelKey).toLowerCase() === label.toLowerCase()
					)
					if (prevIsSameModelIndex) {
						return '#' + id
					}
					label += ' #' + id
				}
			}
			return label
		},
		goToCrumb(crumb) {
			if (!crumb) return
			// push per fullPath: il dedup in TRACK_NAVIGATION tronca il trail al crumb cliccato
			this.$router.push(crumb.fullPath).catch(() => {})
		},
	},
}
</script>

<style scoped>
.breadcrumb-nav {
	background-color: var(--v-background-base) !important;
	border-bottom: 1px solid rgba(0, 0, 0, 0.12);
	min-height: 34px;
	display: flex;
	align-items: center;
	flex-wrap: nowrap;
	overflow: hidden;
	width: 100%;
	padding: 0 12px !important;
}

.breadcrumb-nav >>> li {
	min-width: 0;
}

.breadcrumb-nav >>> li:last-child {
	flex-shrink: 0;
}

.theme--dark .breadcrumb-nav {
	border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.breadcrumb-item {
	font-size: 12px !important;
}

.breadcrumb-nav >>> li {
	padding: 0 4px;
	font-size: 12px;
	line-height: 1.2;
}

/* Variante inline per l'app bar: eredita il colore testo della barra
	(headerColor è personalizzabile per tenant) */
/* il root del componente è il flex item dentro l'app bar: ha lui i vincoli
	di larghezza, la ul interna si adatta */
.breadcrumb-inline-wrap {
	max-width: 40%;
	flex: 0 0 auto;
	overflow: hidden;
	color: inherit;
}

.breadcrumb-inline {
	padding: 0 !important;
	background: transparent;
	flex-wrap: nowrap;
	overflow: hidden;
	max-width: 100%;
	color: inherit;
}

.breadcrumb-inline >>> li {
	padding: 0 2px !important;
	font-size: 13px;
	white-space: nowrap;
	min-width: 0;
}

.breadcrumb-inline >>> li.v-breadcrumbs__divider {
	padding: 0 1px !important;
	color: inherit !important;
}

/* con poco spazio si comprimono (con ellissi) i crumb più vecchi:
	la pagina corrente resta sempre leggibile */
.breadcrumb-inline >>> li:last-child {
	flex-shrink: 0;
}

.breadcrumb-inline >>> .v-breadcrumbs__item {
	color: inherit;
	opacity: 0.8;
}

.breadcrumb-inline >>> .v-breadcrumbs__item--disabled {
	color: inherit !important;
	opacity: 1;
	font-weight: 600;
}

/* il divisore deve seguire il colore del testo della barra anche in dark mode
	(il tema renderebbe il v-icon bianco, invisibile su header chiari) */
.breadcrumb-inline >>> .v-breadcrumbs__divider .v-icon {
	color: inherit !important;
	opacity: 0.6;
}

.breadcrumb-overflow {
	margin: 0 2px;
}

.breadcrumb-mobile {
	background-color: var(--v-background-base);
	border-bottom: 1px solid rgba(0, 0, 0, 0.12);
	min-height: 34px;
	width: 100%;
}

.theme--dark .breadcrumb-mobile {
	border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.breadcrumb-mobile-label {
	font-size: 12px;
	max-width: 80vw;
}

/* L'ultimo crumb (pagina corrente) risalta */
.v-breadcrumbs__item--disabled {
	color: var(--v-primary-base) !important;
	font-weight: 500;
}

/* Troncamento per label lunghe */
.breadcrumb-item >>> .v-breadcrumbs__item {
	max-width: 220px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	display: inline-block;
	font-size: 13px;
	line-height: 1.4;
}
</style>
